import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { WeaponsState, WeaponMount, Weapon } from "./types";

const initialState: WeaponsState = {
  shipSize: 0,
  techLevel: 8,
  weaponMounts: [],
  defensiveSystems: [],
  breakawayHull: {
    isBreakaway: false,
    sections: [],
    combinedPerformance: {},
  },
  isLoading: false,
  error: null,
};

const weaponsSlice = createSlice({
  name: "weapons",
  initialState,
  reducers: {
    // Ship configuration actions
    setShipSize: (state, action: PayloadAction<number>) => {
      state.shipSize = action.payload;
      state.error = null;
    },

    setTechLevel: (state, action: PayloadAction<number>) => {
      state.techLevel = action.payload;
      state.error = null;
    },

    // Weapon mount actions
    addWeaponMount: (state, action: PayloadAction<WeaponMount>) => {
      state.weaponMounts.push(action.payload);
      state.error = null;
    },

    removeWeaponMount: (state, action: PayloadAction<string>) => {
      state.weaponMounts = state.weaponMounts.filter(
        mount => mount.id !== action.payload
      );
      state.error = null;
    },

    updateWeaponMount: (state, action: PayloadAction<{ mountId: string; updates: Partial<WeaponMount> }>) => {
      const { mountId, updates } = action.payload;
      const mountIndex = state.weaponMounts.findIndex(mount => mount.id === mountId);
      if (mountIndex !== -1) {
        state.weaponMounts[mountIndex] = { ...state.weaponMounts[mountIndex], ...updates };
      }
      state.error = null;
    },

    setWeaponMounts: (state, action: PayloadAction<WeaponMount[]>) => {
      state.weaponMounts = action.payload;
      state.error = null;
    },

    // Weapon actions
    addWeaponToMount: (state, action: PayloadAction<{ mountId: string; weapon: Weapon }>) => {
      const { mountId, weapon } = action.payload;
      const mountIndex = state.weaponMounts.findIndex(mount => mount.id === mountId);
      if (mountIndex !== -1) {
        state.weaponMounts[mountIndex].weapons.push(weapon);
      }
      state.error = null;
    },

    removeWeaponFromMount: (state, action: PayloadAction<{ mountId: string; weaponIndex: number }>) => {
      const { mountId, weaponIndex } = action.payload;
      const mountIndex = state.weaponMounts.findIndex(mount => mount.id === mountId);
      if (mountIndex !== -1 && weaponIndex >= 0 && weaponIndex < state.weaponMounts[mountIndex].weapons.length) {
        state.weaponMounts[mountIndex].weapons.splice(weaponIndex, 1);
      }
      state.error = null;
    },

    updateWeaponInMount: (state, action: PayloadAction<{ mountId: string; weaponIndex: number; updates: Partial<Weapon> }>) => {
      const { mountId, weaponIndex, updates } = action.payload;
      const mountIndex = state.weaponMounts.findIndex(mount => mount.id === mountId || mount.type === mountId);
      if (mountIndex !== -1 && weaponIndex >= 0 && weaponIndex < state.weaponMounts[mountIndex].weapons.length) {
        state.weaponMounts[mountIndex].weapons[weaponIndex] = {
          ...state.weaponMounts[mountIndex].weapons[weaponIndex],
          ...updates
        };
      }
      state.error = null;
    },

    // Defensive system actions
    addDefensiveSystem: (state, action: PayloadAction<any>) => {
      state.defensiveSystems.push(action.payload);
      state.error = null;
    },

    removeDefensiveSystem: (state, action: PayloadAction<string>) => {
      state.defensiveSystems = state.defensiveSystems.filter(
        system => system.id !== action.payload
      );
      state.error = null;
    },

    updateDefensiveSystem: (state, action: PayloadAction<{ systemId: string; updates: Partial<any> }>) => {
      const { systemId, updates } = action.payload;
      const systemIndex = state.defensiveSystems.findIndex(system => system.id === systemId);
      if (systemIndex !== -1) {
        state.defensiveSystems[systemIndex] = { ...state.defensiveSystems[systemIndex], ...updates };
      }
      state.error = null;
    },

    setDefensiveSystems: (state, action: PayloadAction<any[]>) => {
      state.defensiveSystems = action.payload;
      state.error = null;
    },

    // Ammunition actions
    updateAmmunition: (state, action: PayloadAction<{ weaponId: string; ammoType: string; quantity: number }>) => {
      const { weaponId, ammoType, quantity } = action.payload;
      // Find weapon in mounts and update ammunition
      state.weaponMounts.forEach(mount => {
        mount.weapons.forEach(weapon => {
          if (weapon.id === weaponId || weapon.name === weaponId) {
            if (!weapon.ammo) {
              weapon.ammo = { 
                type: ammoType, 
                capacity: 0,
                tonsPerReload: 0,
                costPerReload: 0,
                name: "",
                traits: [],
                damage: "",
                tl: 0,
                quantity 
              };
            } else {
              weapon.ammo.type = ammoType;
              weapon.ammo.quantity = quantity;
            }
          }
        });
      });
      state.error = null;
    },

    addAmmunitionReload: (state, action: PayloadAction<{ weaponId: string; ammoType: string }>) => {
      const { weaponId, ammoType } = action.payload;
      // Find weapon in mounts and add reload
      state.weaponMounts.forEach(mount => {
        mount.weapons.forEach(weapon => {
          if (weapon.id === weaponId || weapon.name === weaponId) {
            if (!weapon.ammo) {
              weapon.ammo = { 
                type: ammoType, 
                capacity: 0,
                tonsPerReload: 0,
                costPerReload: 0,
                name: "",
                traits: [],
                damage: "",
                tl: 0,
                quantity: 1, 
                reloads: 1 
              };
            } else {
              weapon.ammo.reloads = (weapon.ammo.reloads || 0) + 1;
            }
          }
        });
      });
      state.error = null;
    },

    removeAmmunitionReload: (state, action: PayloadAction<{ weaponId: string; ammoType: string }>) => {
      const { weaponId, ammoType } = action.payload;
      // Find weapon in mounts and remove reload
      state.weaponMounts.forEach(mount => {
        mount.weapons.forEach(weapon => {
          if ((weapon.id === weaponId || weapon.name === weaponId) && weapon.ammo?.type === ammoType) {
            if (weapon.ammo.reloads && weapon.ammo.reloads > 0) {
              weapon.ammo.reloads -= 1;
            }
          }
        });
      });
      state.error = null;
    },

    // Spinal weapon actions
    setSpinalWeapon: (state, action: PayloadAction<Weapon | null>) => {
      // Find or create spinal mount
      let spinalMount = state.weaponMounts.find(mount => mount.type === "spinalMount");
      if (action.payload) {
        if (spinalMount) {
          spinalMount.weapons = [action.payload];
        } else {
          spinalMount = {
            id: `spinal-${Date.now()}`,
            type: "spinalMount",
            weapons: [action.payload],
            hardpointsUsed: 0,
            range: "Medium",
            power: 0,
            tons: 0,
            cost: 0,
          };
          state.weaponMounts.push(spinalMount);
        }
      } else if (spinalMount) {
        state.weaponMounts = state.weaponMounts.filter(mount => mount.id !== spinalMount!.id && mount.type !== "spinalMount");
      }
      state.error = null;
    },

    updateSpinalWeaponSize: (state, action: PayloadAction<number>) => {
      const spinalMount = state.weaponMounts.find(mount => mount.type === "spinalMount");
      if (spinalMount && spinalMount.weapons.length > 0) {
        const weapon = spinalMount.weapons[0];
        if (weapon.spinalWeapon) {
          weapon.spinalWeapon.sizeMultiple = action.payload;
        }
      }
      state.error = null;
    },

    // Breakaway hull actions
    setBreakawayHull: (state, action: PayloadAction<boolean>) => {
      state.breakawayHull = {
        isBreakaway: action.payload,
        sections: state.breakawayHull?.sections || [],
        combinedPerformance: state.breakawayHull?.combinedPerformance || {},
      };
      state.error = null;
    },

    setBreakawaySections: (state, action: PayloadAction<any[]>) => {
      state.breakawayHull = {
        isBreakaway: state.breakawayHull?.isBreakaway || false,
        sections: action.payload,
        combinedPerformance: state.breakawayHull?.combinedPerformance || {},
      };
      state.error = null;
    },

    updateBreakawayWeapons: (state, action: PayloadAction<{ sectionId: string; weapons: any[] }>) => {
      const { sectionId, weapons } = action.payload;
      if (state.breakawayHull) {
        const sectionIndex = state.breakawayHull.sections.findIndex(section => section.id === sectionId);
        if (sectionIndex !== -1) {
          state.breakawayHull.sections[sectionIndex].weapons = weapons;
        }
      }
      state.error = null;
    },

    updateCombinedWeaponStats: (state, action: PayloadAction<any>) => {
      state.breakawayHull = {
        isBreakaway: state.breakawayHull?.isBreakaway || false,
        sections: state.breakawayHull?.sections || [],
        combinedPerformance: {
          ...state.breakawayHull?.combinedPerformance,
          ...action.payload,
        },
      };
      state.error = null;
    },

    // Utility actions
    clearWeapons: (state) => {
      state.weaponMounts = [];
      state.defensiveSystems = [];
      state.error = null;
    },

    reset: (state) => {
      return { ...initialState, shipSize: state.shipSize, techLevel: state.techLevel };
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      state.error = null;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
});

export const {
  setShipSize,
  setTechLevel,
  addWeaponMount,
  removeWeaponMount,
  updateWeaponMount,
  setWeaponMounts,
  addWeaponToMount,
  removeWeaponFromMount,
  updateWeaponInMount,
  addDefensiveSystem,
  removeDefensiveSystem,
  updateDefensiveSystem,
  setDefensiveSystems,
  updateAmmunition,
  addAmmunitionReload,
  removeAmmunitionReload,
  setSpinalWeapon,
  updateSpinalWeaponSize,
  setBreakawayHull,
  setBreakawaySections,
  updateBreakawayWeapons,
  updateCombinedWeaponStats,
  clearWeapons,
  reset,
  setLoading,
  setError,
} = weaponsSlice.actions;

export default weaponsSlice.reducer;


